import React, { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import cx from 'classnames'
import { NavigationHeaderProps } from '~types'
import { MAX_LINKS_PER_SECTION, NEXT_IMAGE_QUALITY, SWIPE_DISTANCE_LIMIT } from '~constants'
import LiveIcon from 'public/assets/live.svg'
import Link from 'next/link'
import Image from 'next/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import styles from './NavigationHeader.module.scss'
import { disableBodyScroll, getFevoWidgetNameFromUrl, openFevoWidget } from 'utils'
import { NavigationMenuItemProps, SubNavLinksType } from '~types'
import { fireAnalyticsEvent } from '~analyticsUtils'
import { HeaderButton, ConditionalWrapper, HamburgerButton, CloseButton } from '~elements'
import { useOutsideClick, useWindowSize } from 'utils/hooks'
import { Heading } from '~elements'

const NavigationHeader = ({
  brandLogo,
  navigationMenuItems = [],
  buttonTitle,
  buttonUrl,
  buttonIcon,
  isTargetBlank,
  endNavItem,
  endNavItemUrl
}: NavigationHeaderProps) => {
  const { asPath } = useRouter()
  const { isBreakpoint: isBelowMdPlus, windowSizeCalculation } = useWindowSize('md-plus', '<')
  const [pathName, setPathName] = useState(asPath)
  const [openParentNavId, setOpenParentNavId] = useState('')
  const [onChildPage, setOnChildPage] = useState(false)
  const ref = useRef(null)
  const [navDrawerIsOpen, handleNavDrawerToggle] = useState(false)
  const linkModule = 'global-navigation'
  const hasNavMenuItems = navigationMenuItems.length !== 0

  const toggleSidebar = () => {
    handleNavDrawerToggle(!navDrawerIsOpen)
  }

  useEffect(() => {
    disableBodyScroll(navDrawerIsOpen)
  }, [navDrawerIsOpen])

  useEffect(() => {
    setPathName(pathName)
    const childPageArr =
      (navigationMenuItems &&
        navigationMenuItems.map(menuItems =>
          menuItems.subNavLinks?.findIndex(subNavLink => subNavLink.url === asPath)
        )) ||
      []
    const filterIndex = childPageArr.filter(index => (index as number) > -1)
    if ((filterIndex[0] as number) > -1) {
      setOnChildPage(true)
    } else {
      setOnChildPage(false)
      setOpenParentNavId('')
    }
  }, [asPath])

  const closeDrawerHandler = () => {
    if (navDrawerIsOpen) handleNavDrawerToggle(false)
  }

  const handleNavItemClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    title: string,
    url: string,
    parentIndex?: number,
    childIndex?: number
  ) => {
    handleNavDrawerToggle(false)

    const fevoWidgetName = getFevoWidgetNameFromUrl(url)
    if (fevoWidgetName) {
      openFevoWidget(fevoWidgetName, event)
    }

    handleNavItemGTMEvent(url, title, parentIndex, childIndex)
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > SWIPE_DISTANCE_LIMIT && !onChildPage) {
        setOpenParentNavId('')
      }
    }
    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [onChildPage])

  const handleParentNavItemClick = (id: string) => {
    setOpenParentNavId(openParentNavId === id ? '' : id)
  }

  const closeParentNavClickHandler = () => setOpenParentNavId('')

  useOutsideClick(ref, () => closeParentNavClickHandler())

  const activeLink = (item: NavigationMenuItemProps) => {
    if (asPath === item.url) {
      return <span className={asPath === item.url ? styles['is-active-link'] : ''}>{item.title}</span>
    } else if (item.subNavLinks?.find(subNavLink => subNavLink.url === asPath)) {
      const currentChildPath = asPath.split('/')[1].toLowerCase()
      const parentSlug = item.url.slice(1).toLowerCase()
      return currentChildPath === parentSlug ? (
        <span className={styles['is-active-link']}>{item.title}</span>
      ) : (
        <span>{item.title}</span>
      )
    }
    return <span>{item.title}</span>
  }

  const CommonSubMenu = ({
    item,
    parentIndex,
    isMobile,
    asPath,
    handleNavItemClick
  }: {
    item: NavigationMenuItemProps
    parentIndex: number
    isMobile: boolean
    asPath: string
    handleNavItemClick: (
      event: React.MouseEvent<HTMLAnchorElement>,
      title: string,
      url: string,
      parentIndex?: number,
      childIndex?: number
    ) => void
  }) => {
    return (
      <ul className={styles['submenu-links']}>
        {item.subNavLinks?.map((subNavItem: SubNavLinksType, childIndex: number) => {
          const isLastItem = childIndex === item.subNavLinks!.length - 1
          const isNextItemASection = item.subNavLinks![childIndex + 1]?.groupTitle

          if (!subNavItem.groupTitle && subNavItem.url) {
            const elements = (
              <li
                key={subNavItem.id}
                className={asPath === subNavItem.url ? styles['is-current-page'] : ''}
                data-testid='navigation-menu-item'
              >
                <Link
                  href={subNavItem.url}
                  target={subNavItem?.isTargetBlank ? '_blank' : '_self'}
                  onClick={event =>
                    handleNavItemClick(event, subNavItem.title, subNavItem.url, parentIndex, childIndex)
                  }
                >
                  {subNavItem.title}
                </Link>
              </li>
            )

            if (subNavItem.hasDivider) {
              return (
                <React.Fragment key={subNavItem.id}>
                  {elements}
                  <hr key={`${subNavItem.id}-divider`} className={styles['divider']} />
                </React.Fragment>
              )
            } else if (subNavItem.hasAccentDivider) {
              return (
                <React.Fragment key={subNavItem.id}>
                  {elements}
                  <hr key={`${subNavItem.id}-accent-divider`} className={styles['accent-divider']} />
                </React.Fragment>
              )
            }
            return elements
          }
          if (subNavItem.groupTitle) {
            // Only render the top divider if previous subNavItem does NOT have hasDivider or hasAccentDivider
            const prevSubNavItem = childIndex > 0 ? item.subNavLinks![childIndex - 1] : undefined
            const shouldShowTopDivider =
              childIndex > 0 && !(prevSubNavItem?.hasDivider || prevSubNavItem?.hasAccentDivider)
            return (
              <React.Fragment key={subNavItem.id}>
                {shouldShowTopDivider && (
                  <hr
                    key={`${subNavItem.id}-top-divider`}
                    className={isMobile ? styles['submenu-links'] + ' ' + styles['divider'] : styles['divider']}
                  />
                )}
                <li
                  key={`${subNavItem.id}-groupTitle`}
                  className={styles['group-title']}
                  onClick={event => event.stopPropagation()}
                >
                  {subNavItem.groupTitle}
                </li>

                <div className={styles['section']}>
                  {subNavItem.sectionLinks?.slice(0, MAX_LINKS_PER_SECTION).map((linkItem, linkIndex) => (
                    <li key={linkIndex} className={asPath === linkItem.url ? styles['is-current-page'] : ''}>
                      <Link
                        href={linkItem.url}
                        onClick={event =>
                          handleNavItemClick(event, linkItem.title, linkItem.url, parentIndex, linkIndex)
                        }
                      >
                        {linkItem.title}
                      </Link>
                    </li>
                  ))}
                </div>
                {!isNextItemASection && !isLastItem && (
                  <hr
                    key={`${subNavItem.id}-bottom-divider`}
                    className={isMobile ? styles['submenu-links'] + ' ' + styles['divider'] : styles['divider']}
                  />
                )}
              </React.Fragment>
            )
          }
          return null
        })}
      </ul>
    )
  }

  const SubMenu = (
    item: NavigationMenuItemProps,
    parentIndex: number,
    handleNavItemClick: (
      event: React.MouseEvent<HTMLAnchorElement>,
      title: string,
      url: string,
      parentIndex?: number,
      childIndex?: number
    ) => void
  ) => {
    const featureUrl = item?.submenuRedirectUrl || navigationMenuItems[0]?.url

    return (
      <div className={styles['submenu']}>
        <div className={styles['submenu-left']}>
          <CommonSubMenu
            item={item}
            parentIndex={parentIndex}
            isMobile={false}
            asPath={asPath}
            handleNavItemClick={handleNavItemClick}
          />
        </div>
        {featureUrl && (
          <Link
            href={featureUrl}
            className={styles['submenu-feature']}
            onClick={() => {
              fireAnalyticsEvent(featureUrl, asPath, {
                ...sharedAnalyticsProps,
                clickTracking: `${linkModule}:${item?.submenuImageTitle}`
              })
            }}
          >
            {item.submenuImage && (
              <Image
                src={item.submenuImage?.url}
                alt={item?.submenuImageTitle}
                width={256}
                height={160}
                priority
                quality={NEXT_IMAGE_QUALITY}
              />
            )}
            <div className={styles['submenu-text']}>
              {item.submenuImageTitle && <Heading level={4}>{item.submenuImageTitle}</Heading>}
              {item.submenuImageSubtitle && <p>{item.submenuImageSubtitle}</p>}
            </div>
          </Link>
        )}
      </div>
    )
  }

  const SubMenuForMobile = (item: NavigationMenuItemProps, parentIndex: number) => {
    return (
      <div className={styles['submenu']}>
        <CommonSubMenu
          item={item}
          parentIndex={parentIndex}
          isMobile={true}
          asPath={asPath}
          handleNavItemClick={handleNavItemClick}
        />
      </div>
    )
  }

  const BrandLogo = () => {
    return (
      <div className={styles['brand-logo']}>
        {brandLogo && (
          <div className={'image-container'}>
            <Link href={'/'}>
              <Image
                src={brandLogo.url}
                alt='Sphere'
                width='0'
                height='0'
                sizes='100vw'
                priority
                quality={NEXT_IMAGE_QUALITY}
              />
            </Link>
          </div>
        )}
      </div>
    )
  }

  const NavDesktop = () => {
    return (
      <div className={styles['header-global__column']}>
        <div>{BrandLogo()}</div>
        <div className={styles['nav-container']}>
          <nav>{NavMenu()}</nav>
        </div>
        <div className={styles['end-buttons']}>
          {endNavItem && endNavItemUrl && (
            <Link href={endNavItemUrl}>
              <div
                className={styles['nav-link']}
                onClick={() => {
                  handleCtaBtnGTMEvent(endNavItemUrl, endNavItem, '5')
                  toggleSidebar()
                }}
              >
                <LiveIcon />
                <span>{endNavItem}</span>
              </div>
            </Link>
          )}
          {buttonUrl && (
            <HeaderButton
              title={buttonTitle}
              iconName={buttonIcon}
              url={buttonUrl}
              onClick={() => handleCtaBtnGTMEvent(buttonUrl, buttonTitle, '6')}
              isTargetBlank={isTargetBlank}
            />
          )}
        </div>
      </div>
    )
  }

  const sharedAnalyticsProps = {
    event: 'click_nav',
    linkLocation: 'header',
    linkModule: linkModule,
    navType: 'header',
    clickType: 'navigation'
  }

  const handleNavItemGTMEvent = (
    url: string,
    title: string,
    parentIndex: number | undefined,
    childIndex: number | undefined
  ) => {
    fireAnalyticsEvent(url, asPath, {
      ...sharedAnalyticsProps,
      clickTracking: `${linkModule}:${title}`,
      ...(parentIndex &&
        childIndex && {
          linkPosition: `${String(parentIndex + 1)}|${String(childIndex + 1)}`
        })
    })
  }

  const handleCtaBtnGTMEvent = (clickURL: string, title: string | null, index: string) => {
    fireAnalyticsEvent(clickURL, asPath, {
      ...sharedAnalyticsProps,
      clickTracking: `endnavheader:${title}`,
      linkPosition: index
    })
  }

  const NavMobile = () => {
    const handleToggleMenuClick = () => {
      fireAnalyticsEvent(null, asPath, {
        ...sharedAnalyticsProps,
        clickTracking: `${linkModule}:toggle_nav`
      })
      toggleSidebar()
    }

    return (
      <div className={styles['navbar-mobile']}>
        <div className={styles['header-mobile']}>
          {hasNavMenuItems && (
            <div className={styles['nav-drawer-toggler']} data-testid='navbar-mobile-menu'>
              {navDrawerIsOpen ? (
                <CloseButton onClick={handleToggleMenuClick} />
              ) : (
                <HamburgerButton onClick={handleToggleMenuClick} />
              )}
            </div>
          )}
          {BrandLogo()}
          {buttonUrl && (
            <HeaderButton
              title={buttonTitle}
              iconName={buttonIcon}
              url={buttonUrl}
              onClick={() => handleCtaBtnGTMEvent(buttonUrl, buttonTitle, '7')}
              isTargetBlank={isTargetBlank}
            />
          )}
        </div>

        <div
          onClick={e => e.stopPropagation()}
          className={cx(styles['sidebar'], styles[navDrawerIsOpen ? 'open' : ''])}
        >
          <div className={styles['sidebar__scroller']}>
            <nav>
              {NavMenu()}
              {endNavItem && endNavItemUrl && (
                <Link href={endNavItemUrl}>
                  <div
                    className={styles['nav-link']}
                    onClick={() => {
                      handleCtaBtnGTMEvent(endNavItemUrl, endNavItem, '6')
                      toggleSidebar()
                    }}
                  >
                    <LiveIcon />
                    <span>{endNavItem}</span>
                  </div>
                </Link>
              )}
            </nav>
          </div>
        </div>
        <div className={cx(styles['overlay'], styles[navDrawerIsOpen ? 'open' : ''])} onClick={toggleSidebar}></div>
      </div>
    )
  }

  const getSelectedMenu = (item: NavigationMenuItemProps, asPath: string) => {
    let selectedMenu = item.url === asPath ? item.title : null
    if (!selectedMenu && item.subNavLinks && item.subNavLinks.length) {
      const [filteredItem] = item.subNavLinks.filter(subNav => subNav.url === asPath)
      selectedMenu = filteredItem ? item?.title : ''
    }
    return selectedMenu
  }

  const NavMenu = () => {
    return (
      <ul>
        {navigationMenuItems.length
          ? navigationMenuItems.map((item: NavigationMenuItemProps, parentIndex) => {
              if (item.title && item.url) {
                const hasSubmenu = item.subNavLinks && item.subNavLinks.length > 0
                const selectedMenu = getSelectedMenu(item, asPath)

                return (
                  <li
                    key={item.id}
                    onClick={() => handleParentNavItemClick(item.id)}
                    className={cx([
                      styles[openParentNavId === item.id && hasSubmenu ? 'is-open' : ''],
                      styles[selectedMenu === item?.title ? 'active' : '']
                    ])}
                  >
                    <div className={styles['menu-item-wrapper']} data-testid='navigation-menu-subitem'>
                      <ConditionalWrapper
                        condition={!hasSubmenu}
                        wrapper={children => (
                          <Link
                            href={item.url}
                            target={item.isTargetBlank ? '_blank' : '_self'}
                            onClick={event => handleNavItemClick(event, item.title, item.url)}
                          >
                            {children}
                          </Link>
                        )}
                      >
                        {activeLink(item)}
                      </ConditionalWrapper>

                      {hasSubmenu && isBelowMdPlus && (
                        <div className={styles['caret']}>
                          <FontAwesomeIcon icon={faChevronDown} />
                        </div>
                      )}
                    </div>
                    {isBelowMdPlus ? (
                      <>
                        {hasSubmenu && !onChildPage && openParentNavId === item.id && (
                          <>{SubMenuForMobile(item, parentIndex)}</>
                        )}
                        {hasSubmenu && onChildPage && openParentNavId === item.id && (
                          <>{SubMenuForMobile(item, parentIndex)}</>
                        )}
                      </>
                    ) : (
                      hasSubmenu && SubMenu(item, parentIndex, handleNavItemClick)
                    )}
                  </li>
                )
              }
            })
          : null}
      </ul>
    )
  }

  // If the window size has not been calculated
  // return null to avoid rendering issues
  // there was a particular interaction with useWindowSize
  if (!windowSizeCalculation) {
    return null
  }

  return (
    <>
      <header
        ref={ref}
        onClick={closeDrawerHandler}
        className={cx([
          styles['header-global'],
          styles[isBelowMdPlus ? 'header-global--mobile' : 'header-global--desktop'],
          styles[navDrawerIsOpen && isBelowMdPlus ? 'drawer-open' : '']
        ])}
      >
        {isBelowMdPlus ? NavMobile() : NavDesktop()}
      </header>
    </>
  )
}

export default NavigationHeader
